import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FilePlus2,
  FilePenLine,
  MessageSquare,
  Trash2,
  Send,
  Clock,
  Sparkles,
  Activity,
  Inbox,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { Activity as ActivityItem, InboxComment } from '@/lib/types';
import { useAuth } from '@/context/AuthContext';
import { openSearch } from '@/components/SearchPalette';

/* ---------------------------------------------------------------------------
   Feed — route "/" (home).

   The activity landing page. Reverse-chronological stream of what happened
   across the sections this user can see (the API scopes it already), grouped
   by day, plus a "Recently updated" quick list.

     api.feed() → Activity[] = [{ id, actor, verb, nodeId, title, at }]
   --------------------------------------------------------------------------- */

// The feed row shape is the shared `Activity` type from '@/lib/types'.
type FeedItem = ActivityItem;

// ---- relative time ("2h ago", "3d ago") ------------------------------------
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const s = Math.round(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}w ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(d / 365)}y ago`;
}

// ---- day grouping -----------------------------------------------------------
function dayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const today = new Date();
  const startOf = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayMs = 86_400_000;
  const delta = Math.round((startOf(today) - startOf(d)) / dayMs);
  if (delta <= 0) return 'Today';
  if (delta === 1) return 'Yesterday';
  if (delta < 7)
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

// ---- verb → phrasing + icon -------------------------------------------------
function verbMeta(verb: string) {
  const v = verb.toLowerCase();
  if (v.includes('creat') || v.includes('add'))
    return { phrase: 'created', Icon: FilePlus2, tone: 'var(--accent)' };
  if (v.includes('comment'))
    return { phrase: 'commented on', Icon: MessageSquare, tone: '#0ea5e9' };
  if (v.includes('delet') || v.includes('remov'))
    return { phrase: 'deleted', Icon: Trash2, tone: '#ef4444' };
  if (v.includes('publish'))
    return { phrase: 'published', Icon: Send, tone: '#10b981' };
  if (v.includes('updat') || v.includes('edit'))
    return { phrase: 'updated', Icon: FilePenLine, tone: 'var(--ink-muted)' };
  // fall back to the raw verb so nothing is ever swallowed
  return { phrase: v, Icon: Activity, tone: 'var(--ink-muted)' };
}

/**
 * "Good morning" until noon, "Good afternoon" until six, "Good evening" after. Read from
 * the reader's own clock, which is the only one that matters for a greeting.
 */
function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * The home page has two jobs and they are not equal. The first is "is anything waiting
 * on ME" — that is why WaitingOnYou sits above the activity stream and renders nothing
 * at all when the answer is no. The second is "what changed", which is the feed.
 *
 * Ordering matters for the reader this was asked for: Mihran opens the wiki
 * occasionally, not daily, and needs his own items to be the first thing on the page
 * rather than something to find inside a stream of everyone else's edits.
 */
function WaitingOnYou({ items }: { items: InboxComment[] | null }) {
  if (!items || items.length === 0) return null;

  return (
    <section
      className="mb-7 rounded-lg p-4"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
    >
      <div className="mb-3 flex items-center gap-2">
        <Inbox size={14} style={{ color: 'var(--accent)' }} />
        <h2 className="text-[13px] font-semibold" style={{ color: 'var(--ink)' }}>
          Waiting on you
        </h2>
        <span className="text-[12px]" style={{ color: 'var(--ink-faint)' }}>
          {items.length}
        </span>
      </div>
      <ul className="space-y-1">
        {items.slice(0, 6).map((c) => (
          <li key={c.id}>
            <Link
              to={`/doc/${c.nodeId}`}
              className="group block rounded-md px-2 py-2 transition-colors hover:[background:var(--surface-3)]"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className="min-w-0 truncate text-[13px] font-medium underline-offset-2 group-hover:underline"
                  style={{ color: 'var(--ink)' }}
                >
                  {c.nodeTitle}
                </span>
                <span className="shrink-0 text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                  {relativeTime(c.createdAt)}
                </span>
              </div>
              {c.anchor?.quote && (
                <p
                  className="mt-1 truncate border-l-2 pl-2 text-[11px] italic"
                  style={{ borderColor: 'var(--accent)', color: 'var(--ink-faint)' }}
                >
                  {c.anchor.quote}
                </p>
              )}
              <p className="mt-1 line-clamp-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>
                {c.body}
              </p>
              <p className="mt-1 text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                from {c.author.split('@')[0]}
              </p>
            </Link>
          </li>
        ))}
      </ul>
      {items.length > 6 && (
        <p className="mt-2 px-2 text-[12px]" style={{ color: 'var(--ink-faint)' }}>
          and {items.length - 6} more, on their pages.
        </p>
      )}
    </section>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [items, setItems] = useState<FeedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<InboxComment[] | null>(null);

  // Failing to load the inbox must not take the whole home page down with it — the
  // panel simply doesn't render, and the feed below is unaffected.
  useEffect(() => {
    let alive = true;
    api
      .commentInbox()
      .then((d) => alive && setWaiting(d))
      .catch(() => alive && setWaiting([]));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.feed();
        if (!alive) return;
        setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!alive) return;
        setError('Could not load your feed. Please try again.');
        setItems([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // newest first, grouped by day
  const groups = useMemo(() => {
    if (!items) return [];
    const sorted = [...items].sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
    const out: { label: string; items: FeedItem[] }[] = [];
    for (const it of sorted) {
      const label = dayLabel(it.at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(it);
      else out.push({ label, items: [it] });
    }
    return out;
  }, [items]);

  // "Recently updated": most-recent distinct pages
  const recent = useMemo(() => {
    if (!items) return [];
    const sorted = [...items].sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
    const seen = new Set<string>();
    const out: FeedItem[] = [];
    for (const it of sorted) {
      if (seen.has(it.nodeId)) continue;
      seen.add(it.nodeId);
      out.push(it);
      if (out.length >= 6) break;
    }
    return out;
  }, [items]);

  return (
    <div className="mx-auto w-full max-w-[1080px] px-4 py-8 sm:px-6">
      <header className="mb-6">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--ink)' }}>
          {greeting()}
          {user?.name ? `, ${user.name}` : ''}
        </h1>
        <p className="mt-0.5 text-sm" style={{ color: 'var(--ink-muted)' }}>
          {waiting && waiting.length > 0
            ? waiting.length === 1
              ? 'One comment is waiting on you.'
              : `${waiting.length} comments are waiting on you.`
            : "Nothing is waiting on you. Here's what changed."}
        </p>
      </header>

      <div className="flex flex-col gap-8 lg:flex-row">
        {/* main feed */}
        <div className="min-w-0 flex-1">
          <WaitingOnYou items={waiting} />
          {items === null ? (
            <FeedSkeleton />
          ) : error && items.length === 0 ? (
            <ErrorState message={error} />
          ) : groups.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-7">
              {groups.map((g) => (
                <section key={g.label}>
                  <h2
                    className="mb-2 text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: 'var(--ink-faint)' }}
                  >
                    {g.label}
                  </h2>
                  <ul className="space-y-0.5">
                    {g.items.map((it) => (
                      <FeedRow key={it.id} item={it} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        {/* recently updated */}
        <aside className="w-full shrink-0 lg:w-72">
          <div
            className="rounded-lg p-4"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
          >
            <div className="mb-3 flex items-center gap-2">
              <Clock size={14} style={{ color: 'var(--ink-faint)' }} />
              <h2 className="text-[13px] font-semibold" style={{ color: 'var(--ink)' }}>
                Recently updated
              </h2>
            </div>
            {items === null ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-4 animate-pulse rounded"
                    style={{ background: 'var(--surface-3)', width: `${90 - i * 12}%` }}
                  />
                ))}
              </div>
            ) : recent.length === 0 ? (
              <p className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>
                Nothing here yet.
              </p>
            ) : (
              <ul className="space-y-1">
                {recent.map((it) => (
                  <li key={it.nodeId}>
                    <Link
                      to={`/doc/${it.nodeId}`}
                      className="flex items-baseline justify-between gap-2 rounded px-2 py-1.5 text-[13px] transition-colors hover:[background:var(--surface-3)]"
                    >
                      <span className="min-w-0 truncate" style={{ color: 'var(--ink)' }}>
                        {it.title}
                      </span>
                      <span className="shrink-0 text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                        {relativeTime(it.at)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---- one feed row -----------------------------------------------------------
function FeedRow({ item }: { item: FeedItem }) {
  const { phrase: verbPhrase, Icon, tone } = verbMeta(item.verb);
  // One verb ('commented') now covers posting, replying, resolving, reopening and
  // assigning, so for those rows prefer the server's summary. Only comment rows get
  // this treatment: verbMeta's other phrases already read correctly before the title
  // ("created", "updated"), and appending "on" to them would not.
  const isComment = item.verb.toLowerCase().includes('comment');
  const phrase = isComment && item.summary ? `${item.summary} on` : verbPhrase;
  return (
    <li>
      <Link
        to={`/doc/${item.nodeId}`}
        className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:[background:var(--surface-3)]"
      >
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: tone }}
        >
          <Icon size={14} />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm" style={{ color: 'var(--ink-muted)' }}>
          <span className="font-medium" style={{ color: 'var(--ink)' }}>
            {item.actor}
          </span>{' '}
          {phrase}{' '}
          <span className="font-medium underline-offset-2 group-hover:underline" style={{ color: 'var(--ink)' }}>
            {item.title}
          </span>
        </span>
        <span className="shrink-0 text-[12px]" style={{ color: 'var(--ink-faint)' }}>
          {relativeTime(item.at)}
        </span>
      </Link>
    </li>
  );
}

// ---- states -----------------------------------------------------------------
function FeedSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 2 }).map((_, g) => (
        <div key={g}>
          <div
            className="mb-3 h-3 w-16 animate-pulse rounded"
            style={{ background: 'var(--surface-3)' }}
          />
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                <div className="h-7 w-7 shrink-0 animate-pulse rounded-full" style={{ background: 'var(--surface-3)' }} />
                <div className="h-4 flex-1 animate-pulse rounded" style={{ background: 'var(--surface-3)' }} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-xl px-6 py-16 text-center"
      style={{ background: 'var(--surface)', border: '1px dashed var(--border)' }}
    >
      <span
        className="grid h-12 w-12 place-items-center rounded-full"
        style={{ background: 'var(--surface-2)', color: 'var(--accent)' }}
      >
        <Sparkles size={22} />
      </span>
      <h2 className="text-base font-semibold" style={{ color: 'var(--ink)' }}>
        Nothing yet — create your first page
      </h2>
      <p className="max-w-sm text-sm" style={{ color: 'var(--ink-muted)' }}>
        This is your home feed. As you and your teammates create, edit, and
        comment on pages, activity shows up here.
      </p>
      <div className="mt-1 flex items-center gap-2">
        <Link to="/doc/new" className="btn btn-primary">
          <FilePlus2 size={15} /> New page
        </Link>
        <button type="button" className="btn btn-outline" onClick={() => openSearch()}>
          Search the wiki
        </button>
      </div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div
      className="rounded-xl px-6 py-12 text-center text-sm"
      style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--ink-muted)' }}
    >
      {message}
    </div>
  );
}
